import { Users, HeartHandshake, Shirt, Plane, CalendarRange, Dumbbell, Building2 } from "lucide-react";
import { useFetch, fileUrl, usePageTitle } from "@/lib/api";
import { SPONSOR_OPPS, IMG } from "@/lib/content";
import { PageHero, SectionHeading, Reveal, EmptyState } from "@/components/site/Bits";
import { InquiryForm } from "@/components/site/InquiryForm";

const ICONS = [Users, HeartHandshake, Shirt, Plane, CalendarRange, Dumbbell, Building2];

export default function Sponsors() {
  usePageTitle("Partner With the Coast");
  const { data } = useFetch("/content/sponsors");
  const sponsors = data || [];
  const news = sponsors.filter((s) => s.announcement);
  return (
    <div data-testid="sponsors-page">
      <PageHero testid="sponsors-hero" eyebrow="Sponsorship & Partnerships" image={IMG.coast}
        title={<>Partner With<br /><span className="text-gold">The Coast.</span></>}
        sub="Local businesses and community organizations make it possible for Treasure Coast athletes to compete on a bigger stage." />

      <section className="bg-pitch py-16 sm:py-24">
        <div className="wrap">
          <SectionHeading eyebrow="Opportunities" title="Ways to Support" sub="Every partnership is customized with Coach KP. Tell us how you'd like to get involved." />
          <div className="mt-12 grid gap-px bg-line/60 sm:grid-cols-2 lg:grid-cols-4">
            {SPONSOR_OPPS.map((o, i) => {
              const Icon = ICONS[i];
              return (
                <Reveal key={o.key} delay={(i % 4) * 0.06} className="bg-pitch p-7 hover:bg-navy transition-colors">
                  <div data-testid={`sponsor-opp-${i}`}>
                    <Icon className="h-8 w-8 text-gold" />
                    <p className="mt-5 font-display text-2xl font-black uppercase italic leading-tight">{o.key}</p>
                    <p className="mt-2 text-sm text-slate-400">{o.body}</p>
                  </div>
                </Reveal>
              );
            })}
            <div className="bg-gold text-navy p-7 flex flex-col justify-end">
              <p className="font-display text-3xl font-black uppercase italic leading-none">Invest in the Coast's best.</p>
              <a href="#sponsor-inquiry" className="mt-4 font-semibold uppercase text-sm tracking-wider underline" data-testid="sponsor-inquiry-anchor">Start the conversation →</a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-navy py-16 sm:py-24">
        <div className="wrap">
          <SectionHeading eyebrow="Approved Partners" title="Our Sponsors" />
          <div className="mt-10">
            {sponsors.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-line/60" data-testid="sponsor-wall">
                {sponsors.map((s) => (
                  <a key={s.id} href={s.website || undefined} target="_blank" rel="noreferrer" className="flex flex-col h-40 items-center justify-center gap-2 bg-navy p-6 hover:bg-pitch transition-colors" data-testid={`sponsor-${s.id}`}>
                    {s.logo_url ? <img src={fileUrl(s.logo_url)} alt={s.name} className="max-h-16 max-w-full object-contain" /> : <span className="font-display text-xl font-black uppercase italic text-center">{s.name}</span>}
                    {s.level && <span className="font-mono text-[10px] uppercase tracking-widest text-gold">{s.level}</span>}
                  </a>
                ))}
              </div>
            ) : <EmptyState testid="sponsors-empty" title="Be the first partner of the 2027 season." body="Approved sponsor logos and partnership announcements will be displayed here." />}
          </div>
          {news.length > 0 && (
            <div className="mt-12 grid gap-4 md:grid-cols-2" data-testid="partnership-announcements">
              {news.map((s) => <div key={s.id} className="card-navy p-6"><p className="eyebrow mb-2">Partnership Announcement</p><p className="font-display text-2xl font-black uppercase italic">{s.name}</p><p className="mt-2 text-sm text-slate-300">{s.announcement}</p></div>)}
            </div>
          )}
        </div>
      </section>

      <section id="sponsor-inquiry" className="bg-pitch py-16 sm:py-24 scroll-mt-28">
        <div className="wrap grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading eyebrow="Sponsorship Inquiry" title="Let's Build Something Together" sub="Submit your inquiry and it will be sent directly to Coach KP at kelvinpollock370@icloud.com." />
          <InquiryForm type="sponsorship" prefix="sponsor" />
        </div>
      </section>
    </div>
  );
}
